import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import "./manage-patients.css";
import Loading from "../loading/Loading";

const ManagePatients = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [patientsPerPage, setPatientsPerPage] = useState(6);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageInput, setPageInput] = useState("1");
  const [sortOption, setSortOption] = useState("Filter by patients");
  const [allPatients, setAllPatients] = useState([]);

  const PATIENTS_API_ENDPOINT = "/wp-json/dbsb/v1/user/patients";

  // Fetch patients data
  useEffect(() => {
    const fetchPatients = async () => {
      setIsLoading(true);
      setError(null);
      try {
        if (!window.wpApiSettings?.nonce) {
          throw new Error(
            "Authentication token not found. Please try logging in again."
          );
        }

        const response = await fetch(PATIENTS_API_ENDPOINT, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings.nonce,
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error("You must be logged in to view patients.");
          } else if (response.status === 403) {
            throw new Error("You don't have permission to view patients.");
          }
          throw new Error("Failed to fetch patients.");
        }

        const data = await response.json();
        const mappedPatients = data.patients.map((patient) => ({
          id: patient.id.toString(),
          firstName: patient.first_name || "Unknown",
          lastName: patient.last_name || "",
          email: patient.email || "N/A",
          testsOrdered: patient.published_order_count || 0,
          createdAt: patient.created_at || new Date().toISOString(),
        }));

        setPatients(mappedPatients);
        setFilteredPatients(mappedPatients);
        setAllPatients(mappedPatients);
      } catch (error) {
        console.error("Error fetching patients:", error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPatients();
  }, []);

  // Apply search and sort
  useEffect(() => {
    let filtered = [...allPatients];

    // Apply search
    if (searchQuery) {
      filtered = filtered.filter(
        (patient) =>
          patient.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
          `${patient.firstName} ${patient.lastName}`
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
      );
    }

    // Apply sorting
    if (sortOption === "Filter by patients") {
      filtered.sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB); // A-Z (ascending)
      });
    } else if (sortOption === "Filter by Tests ordered") {
      filtered.sort((a, b) => b.testsOrdered - a.testsOrdered); // Most to least (descending)
    } else if (sortOption === "Filter by Date added") {
      filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // Most recent to least recent (descending)
    }

    setFilteredPatients(filtered);
    setCurrentPage(1); // Reset to page 1 on filter/sort
    setPageInput("1");
  }, [searchQuery, sortOption, allPatients]);

  const totalFilteredPatients = filteredPatients.length;
  const totalPages = Math.ceil(totalFilteredPatients / patientsPerPage);
  const startIndex = (currentPage - 1) * patientsPerPage + 1;
  const endIndex = Math.min(
    startIndex + patientsPerPage - 1,
    totalFilteredPatients
  );
  const currentPatients = filteredPatients.slice(
    (currentPage - 1) * patientsPerPage,
    currentPage * patientsPerPage
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setPageInput(page.toString());
    }
  };

  const handlePageInputChange = (e) => {
    const value = e.target.value;
    setPageInput(value);
    const page = parseInt(value);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePatientsPerPageChange = (e) => {
    const value = parseInt(e.target.value);
    setPatientsPerPage(value);
    setCurrentPage(1);
    setPageInput("1");
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { when: "beforeChildren", staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 300, damping: 20 },
    },
  };

  const rowVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.05,
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    }),
    hover: {
      backgroundColor: "rgba(58, 63, 106, 0.02)",
      transition: { duration: 0.2 },
    },
  };

  const buttonVariants = {
    hover: { scale: 1.05, transition: { duration: 0.2 } },
    tap: { scale: 0.95, transition: { duration: 0.1 } },
  };

  const searchVariants = {
    rest: { scale: 1, boxShadow: "0px 0px 0px rgba(0, 0, 0, 0)" },
    focus: {
      scale: 1.01,
      boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
      transition: { type: "spring", stiffness: 400, damping: 20 },
    },
  };

  const profilePicVariants = {
    hover: {
      scale: 1.1,
      transition: { type: "spring", stiffness: 300, damping: 10 },
    },
  };

  return (
    <motion.div
      className="manage-patients-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        className="manage-patients-header-container"
        variants={itemVariants}
      >
        <div className="header-left">
          <h1>Manage patients</h1>
          <p>View and manage patient information and test orders.</p>
        </div>
        <motion.button
          className="add-patient-button"
          onClick={() => navigate("/add-patient")}
          variants={buttonVariants}
          whileHover="hover"
          whileTap="tap"
        >
          Add new patient
        </motion.button>
      </motion.div>

      <motion.div className="manage-patient-card" variants={itemVariants}>
        {isLoading ? (
          <motion.div
            className="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Loading />
          </motion.div>
        ) : error ? (
          <motion.div
            className="error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {error}
          </motion.div>
        ) : allPatients.length === 0 ? (
          <motion.div
            className="no-patients"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, type: "spring", stiffness: 200 }}
          >
            <motion.img
              src={`${process.env.PUBLIC_URL}/assets/no-patients.png`}
              alt="No patients"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: 0.5,
                delay: 0.1,
                type: "spring",
                stiffness: 200,
              }}
            />
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              No patients yet.
            </motion.p>
          </motion.div>
        ) : (
          <>
            <motion.div
              className="card-header"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <motion.div
                className="search-bar"
                variants={searchVariants}
                initial="rest"
                whileFocus="focus"
                whileHover="focus"
              >
                <Search size={20} color="#656E71" />
                <input
                  type="text"
                  placeholder="Search by patient name or id"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </motion.div>
              <div className="pagination-filter">
                <motion.div
                  className="pagination-info"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <span>
                    {startIndex}-
                    {endIndex < totalFilteredPatients
                      ? endIndex
                      : totalFilteredPatients}
                    <span>/{totalFilteredPatients} patients</span>
                  </span>
                </motion.div>
                <div className="filter-select-wrapper">
                  <svg
                    width="25"
                    height="26"
                    viewBox="0 0 20 21"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.9406 16.3885C11.9406 16.8969 11.6073 17.5635 11.1823 17.8219L10.0073 18.5802C8.91563 19.2552 7.39896 18.4969 7.39896 17.1469V12.6885C7.39896 12.0969 7.06562 11.3385 6.72396 10.9219L3.52394 7.5552C3.09894 7.1302 2.76562 6.38021 2.76562 5.87188V3.93854C2.76562 2.9302 3.52397 2.17188 4.44897 2.17188H15.5656C16.4906 2.17188 17.249 2.9302 17.249 3.8552V5.7052C17.249 6.3802 16.824 7.22187 16.4073 7.63854"
                      stroke="#31383B"
                      stroke-width="1.25"
                      stroke-miterlimit="10"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M13.3932 14.263C14.866 14.263 16.0599 13.0691 16.0599 11.5964C16.0599 10.1236 14.866 8.92969 13.3932 8.92969C11.9205 8.92969 10.7266 10.1236 10.7266 11.5964C10.7266 13.0691 11.9205 14.263 13.3932 14.263Z"
                      stroke="#31383B"
                      stroke-width="1.25"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M16.5599 14.763L15.7266 13.9297"
                      stroke="#31383B"
                      stroke-width="1.25"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <motion.select
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value)}
                  >
                    <option value="Filter by patients">
                      Filter by patients
                    </option>
                    <option value="Filter by Tests ordered">
                      Filter by Tests ordered
                    </option>
                    <option value="Filter by Date added">
                      Filter by Date added
                    </option>
                  </motion.select>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="patients-grid"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div
                className="table-header"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <span>Name</span>
                <span>Patient ID</span>
                <span>Tests ordered</span>
              </motion.div>
              <motion.div
                className="table-body"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <AnimatePresence>
                  {currentPatients.map((patient, index) => (
                    <motion.div
                      key={patient.id}
                      className="table-row"
                      variants={rowVariants}
                      initial="hidden"
                      animate="visible"
                      exit={{
                        opacity: 0,
                        x: 20,
                        transition: { duration: 0.2 },
                      }}
                      whileHover="hover"
                      custom={index}
                    >
                      <div className="patient-info">
                        <motion.div
                          className="profile-pic"
                          variants={profilePicVariants}
                          whileHover="hover"
                        >
                          {patient.firstName.charAt(0)}
                        </motion.div>
                        <div className="name-email-container">
                          <motion.span
                            className="patient-name-value"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.1 + index * 0.05 }}
                          >
                            {patient.firstName} {patient.lastName}
                          </motion.span>
                          <motion.span
                            className="patient-email-value"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 + index * 0.05 }}
                          >
                            {patient.email}
                          </motion.span>
                        </div>
                      </div>
                      <motion.span
                        className="patientid"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 + index * 0.05 }}
                      >
                        {patient.id}
                      </motion.span>
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 + index * 0.05 }}
                      >
                        {patient.testsOrdered === 0
                          ? "None"
                          : patient.testsOrdered}
                      </motion.span>
                      <motion.button
                        className="view-button"
                        onClick={() =>
                          navigate(`/patient-details/${patient.id}`)
                        }
                        whileHover={{ color: "#222952" }}
                        whileTap={{ scale: 0.95 }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 + index * 0.05 }}
                      >
                        View details
                      </motion.button>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </motion.div>
            </motion.div>

            <motion.div
              className="pagination"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <div className="page-selector">
                <motion.input
                  type="text"
                  value={pageInput}
                  onChange={handlePageInputChange}
                  className={parseInt(pageInput) > totalPages ? "invalid" : ""}
                  whileFocus={{
                    scale: 1.05,
                    boxShadow: "0px 0px 5px rgba(58, 63, 106, 0.2)",
                  }}
                  transition={{ duration: 0.2 }}
                />
                <span>of {totalPages} pages</span>
              </div>
              <div className="page-arrows">
                <motion.button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  variants={buttonVariants}
                  whileHover={currentPage !== 1 ? "hover" : {}}
                  whileTap={currentPage !== 1 ? "tap" : {}}
                >
                  <ChevronLeft className="arrow-button-icon" size={18} />
                </motion.button>
                <motion.button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  variants={buttonVariants}
                  whileHover={currentPage !== totalPages ? "hover" : {}}
                  whileTap={currentPage !== totalPages ? "tap" : {}}
                >
                  <ChevronRight className="arrow-button-icon" size={18} />
                </motion.button>
              </div>
              <div className="patients-per-page">
                <span>Patients per page:</span>
                <div className="select-wrapper">
                  <motion.select
                    value={patientsPerPage}
                    onChange={handlePatientsPerPageChange}
                    whileFocus={{ scale: 1.05 }}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  >
                    <option value={6}>6</option>
                    <option value={7}>7</option>
                    <option value={8}>8</option>
                    <option value={9}>9</option>
                    <option value={10}>10</option>
                  </motion.select>
                  <ChevronDown size={16} color="#3A3F6A" />
                </div>
              </div>
            </motion.div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

export default ManagePatients;
